import { Controller } from "@hotwired/stimulus"

// Manages PWA install prompt banner for operator layout.
// Intercepts beforeinstallprompt (Android/Chrome), shows iOS fallback instructions.
// 30-day dismiss cooldown via localStorage.
export default class extends Controller {
  static targets = ["banner", "iosInstructions"]

  connect() {
    // Already installed — don't show
    if (window.matchMedia("(display-mode: standalone)").matches || navigator.standalone) {
      return
    }

    // Check if user dismissed recently (30-day cooldown)
    const dismissed = localStorage.getItem("logix-install-dismissed")
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10)
      const thirtyDays = 30 * 24 * 60 * 60 * 1000
      if (Date.now() - dismissedAt < thirtyDays) {
        return
      }
    }

    this.deferredPrompt = null
    this.promptReceived = false

    // Android/Chrome: intercept beforeinstallprompt
    this.beforeInstallHandler = (e) => {
      e.preventDefault()
      this.deferredPrompt = e
      this.promptReceived = true
      this.showBanner()
    }
    window.addEventListener("beforeinstallprompt", this.beforeInstallHandler)

    // iOS Safari detection: defer to allow beforeinstallprompt to fire first
    if (this.isIOS() && !this.isStandalone()) {
      setTimeout(() => {
        if (!this.promptReceived) {
          this.showIOSBanner()
        }
      }, 500)
    }
  }

  disconnect() {
    if (this.beforeInstallHandler) {
      window.removeEventListener("beforeinstallprompt", this.beforeInstallHandler)
    }
  }

  showBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove("hidden")
    }
  }

  showIOSBanner() {
    if (this.hasIosInstructionsTarget) {
      this.iosInstructionsTarget.classList.remove("hidden")
    }
  }

  async install() {
    if (!this.deferredPrompt) return

    this.deferredPrompt.prompt()
    const { outcome } = await this.deferredPrompt.userChoice

    if (outcome === "accepted") {
      document.dispatchEvent(new CustomEvent("toast:show", {
        detail: { message: "App installed successfully!", type: "success" }
      }))
    }

    this.deferredPrompt = null
    this.hideBanner()
  }

  dismiss() {
    localStorage.setItem("logix-install-dismissed", Date.now().toString())
    this.hideBanner()
  }

  dismissIOS() {
    localStorage.setItem("logix-install-dismissed", Date.now().toString())
    if (this.hasIosInstructionsTarget) {
      this.iosInstructionsTarget.classList.add("hidden")
    }
  }

  hideBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("hidden")
    }
  }

  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
  }

  isStandalone() {
    return window.matchMedia("(display-mode: standalone)").matches || navigator.standalone
  }
}
